// Sistema de comunicación entre ventanas usando únicamente localStorage.
// La ventana que emite NO recibe su propio evento — AppContext y consumidores
// ya aplican el cambio localmente antes de emitir.

import { STORAGE_KEYS } from './localStorage';

export interface BracketField {
  id: string;
  name: string;
  team1: string;
  team2: string;
  winner: string;
  score?: {
    team1: number;
    team2: number;
  };
}

export interface BracketPhase {
  id: string;
  title: string;
  fields: BracketField[];
  createdAt?: number;
  description?: string;
}

export interface TreeBracketField {
  team1: string;
  team2: string;
  winner: string;
  name?: string;
}

export interface TreeBracket {
  semifinal: {
    field1: TreeBracketField;
    field2: TreeBracketField;
  };
  final: {
    winner: string;
  };
}

export interface AppConfig {
  timer: {
    minutes: number;
    seconds: number;
    title: string;
    subtitle: string;
    categories: Array<{
      id: string;
      name: string;
      minutes: number;
      seconds: number;
      color?: string;
      isActive?: boolean;
      syncWithMain?: boolean;
      intro?: {
        title: string;
        subtitle: string;
        duration: number;
      };
    }>;
  };
  bracket: {
    title: string;
    teams: Array<{
      name: string;
      color?: string;
      image?: string;
    }>;
    tournamentType?: 'single' | 'double';
    allowScores?: boolean;
    workingFields?: BracketField[];
    displayFields?: BracketField[];
    fields?: BracketField[];
    phases?: BracketPhase[];
    currentPhase?: string;
    activePhaseId?: string;
    editingSnapshot?: boolean;
    displayMode?: 'working' | 'snapshot';
    treeBracket?: TreeBracket;
    bracketMode?: 'phases' | 'tree';
  };
  leaderboard: {
    title: string;
    maxVisiblePositions?: number;
    autoFit?: boolean;
    phaseNames: {
      [key: string]: string;
    };
    teams: Array<{
      name: string;
      color?: string;
      image?: string;
      phases: {
        [key: string]: number;
      };
      total: number;
    }>;
  };
  carousel: {
    autoPlay: boolean;
    interval: number;
    mode: 'sequential' | 'random';
    transition: 'fade' | 'slide' | 'zoom';
    showControls: boolean;
    media: Array<{
      id: string;
      type: 'image' | 'video';
      url: string;
      duration?: number;
      name?: string;
      size?: number;
      uploaded?: boolean;
    }>;
  };
  podium: {
    title: string;
    subtitle: string;
    teams: {
      first?: string;
      second?: string;
      third?: string;
    };
    teamImages: {
      [teamName: string]: string;
    };
    showPodium: boolean;
    currentSlide: number;
  };
  pizarra: {
    title: string;
    subtitle: string;
    pizarras: Array<{
      id: string;
      teamName: string;
      number: number;
      score: number;
      color?: string;
    }>;
    showNumbers: boolean;
    showTeamNames: boolean;
    showScores: boolean;
  };
}

export type SocketStatus = 'connected' | 'local';

export interface SocketStatusUpdate {
  status: SocketStatus;
  message: string;
  timestamp: number;
}

export type SocketListener = (data: any) => void;
export type StatusListener = (update: SocketStatusUpdate) => void;

const EVENT_PREFIX = STORAGE_KEYS.EVENT_PREFIX;
const EVENT_MAX_AGE_MS = 30_000; // coincide con cleanOldEvents
const hasWindow = (): boolean => typeof window !== 'undefined';

class LocalStorageService {
  private listeners: { [event: string]: SocketListener[] } = {};
  private statusListeners: StatusListener[] = [];
  private currentStatus: SocketStatus = 'local';
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private storageHandler: ((e: StorageEvent) => void) | null = null;

  constructor() {
    if (!hasWindow()) return;
    this.setupStorageListener();
    this.startStatusCheck();
    this.updateStatus('connected', 'Sistema de comunicación activo');
  }

  private setupStorageListener(): void {
    if (!hasWindow()) return;
    // Idempotente: si ya hay listener, no registramos otro. Necesario porque
    // `connect()` se puede llamar múltiples veces (p.ej. tras StrictMode o HMR).
    if (this.storageHandler) return;
    this.storageHandler = (e: StorageEvent) => {
      if (!e.key || !e.newValue) return;
      if (!e.key.startsWith(EVENT_PREFIX)) return;
      try {
        const eventData = JSON.parse(e.newValue);
        const eventName = e.key.slice(EVENT_PREFIX.length);
        if (typeof eventData?.timestamp === 'number' && Date.now() - eventData.timestamp < EVENT_MAX_AGE_MS) {
          this.triggerLocalListeners(eventName, eventData.data);
        }
      } catch (error) {
        console.error('Error procesando evento:', error);
      }
    };
    window.addEventListener('storage', this.storageHandler);
  }

  private startStatusCheck(): void {
    if (this.checkInterval) clearInterval(this.checkInterval);
    this.checkInterval = setInterval(() => this.cleanOldEvents(), 10_000);
  }

  private cleanOldEvents(): void {
    if (!hasWindow()) return;
    const now = Date.now();
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(EVENT_PREFIX)) keys.push(key);
    }
    keys.forEach(key => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (typeof data?.timestamp !== 'number' || now - data.timestamp > EVENT_MAX_AGE_MS) {
          localStorage.removeItem(key);
        }
      } catch {
        // Entrada corrupta — la removemos.
        localStorage.removeItem(key);
      }
    });
  }

  private updateStatus(status: SocketStatus, message: string): void {
    this.currentStatus = status;
    const statusUpdate: SocketStatusUpdate = { status, message, timestamp: Date.now() };
    this.statusListeners.forEach(listener => {
      try {
        listener(statusUpdate);
      } catch (error) {
        console.error('Error en listener de estado:', error);
      }
    });
  }

  private triggerLocalListeners(event: string, data: any): void {
    const callbacks = this.listeners[event];
    if (!callbacks) return;
    // Copiamos por si algún callback se des-registra durante el iter.
    [...callbacks].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error en listener de '${event}':`, error);
      }
    });
  }

  getStatus(): SocketStatus {
    return this.currentStatus;
  }

  onStatusChange(callback: StatusListener): () => void {
    this.statusListeners.push(callback);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  connect(): void {
    // Re-asegura que el listener y el chequeo periódico estén activos.
    // En React StrictMode el cleanup del AppProvider puede haber llamado a
    // disconnect() entre los dos montajes; connect() debe dejar el servicio
    // operativo sin importar el estado previo.
    this.setupStorageListener();
    if (!this.checkInterval) this.startStatusCheck();
    this.updateStatus('connected', 'Comunicación entre ventanas activa');
  }

  emit(event: string, data: any): void {
    if (!hasWindow()) return;
    const eventData = {
      data,
      timestamp: Date.now(),
      source: window.location.href,
      id: Math.random().toString(36).slice(2, 11)
    };
    try {
      localStorage.setItem(`${EVENT_PREFIX}${event}`, JSON.stringify(eventData));
    } catch (error) {
      console.error(`Error enviando evento '${event}':`, error);
    }
  }

  on(event: string, callback: SocketListener): void {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  off(event: string, callback?: SocketListener): void {
    if (!this.listeners[event]) return;
    if (callback) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    } else {
      delete this.listeners[event];
    }
  }

  disconnect(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.storageHandler && hasWindow()) {
      window.removeEventListener('storage', this.storageHandler);
      this.storageHandler = null;
    }
    this.updateStatus('local', 'Sistema desconectado');
  }

  forceLocalMode(): void {
    this.connect();
  }

  isLocalMode(): boolean {
    return true;
  }
}

export const socketService = new LocalStorageService();
